import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * A reusable combobox/autocomplete component.
 * Uses React Portal to render the dropdown outside of the parent container to avoid overflow issues.
 */
const Combobox = ({ items, value, onChange, onInputChange, placeholder, className, inputClassName, inputStyle, labelKey = 'label', valueKey = 'id', searchKeys, disableFiltering = false, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef(null);

    // Sync query with external value
    // We only update the query if the value prop changes AND it's different from current query
    useEffect(() => {
        if (value !== undefined && value !== null) {
            // Try to find the matching item to get its label
            const matchedItem = items?.find(item => String(item[valueKey]) === String(value));
            const displayValue = matchedItem ? getLabel(matchedItem) : value;

            if (displayValue !== query) {
                setQuery(displayValue);
            }
        } else if ((value === '' || value === null) && query !== '') {
            setQuery('');
        }
    }, [value, items]);

    // Handle clicks outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                // Also check if click is inside the portal dropdown (not easy with direct portal, but pointer-events handle it usually)
                // Actually, since portal is in body, we need to check if target is inside portal-dropdown-root?
                // Simpler: Just rely on blur or specific logic.
                // For now, let's delay closing to allow click on portal item
                // Using a slightly different approach:
            }
        };
        // Global click listener is tricky with Portals. 
        // Better: Listen to clicks on body and check if target is inside container OR inside the dropdown class.

        const handleGlobalClick = (e) => {
            // Check if click is inside input container
            if (containerRef.current && containerRef.current.contains(e.target)) return;

            // Check if click is inside a dropdown item (we'll add a specific class or ID)
            if (e.target.closest('.combobox-portal-dropdown')) return;

            setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleGlobalClick);
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true); // Capture scroll
        }

        return () => {
            document.removeEventListener('mousedown', handleGlobalClick);
            window.removeEventListener('resize', updateCoords);
            window.removeEventListener('scroll', updateCoords, true);
        };
    }, [isOpen]);

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    const handleFocus = () => {
        if (disabled) return;
        setQuery(''); // Clear text on focus to restart search

        updateCoords();
        setIsOpen(true);
    };

    const handleMouseDown = (e) => {
        if (disabled) return;
        if (document.activeElement === e.target) {
            setIsOpen(!isOpen);
        }
    };

    const getLabel = (item) => {
        if (!item) return '';
        if (typeof labelKey === 'function') return labelKey(item);
        return item[labelKey] || '';
    };

    const filteredItems = (!items || query === '' || disableFiltering)
        ? (items || [])
        : items.filter((item) => {
            const keysToSearch = searchKeys || [labelKey];
            const q = (query || '').toString().toLowerCase().trim();

            return keysToSearch.some(key => {
                const val = typeof key === 'function' ? key(item) : item[key];
                return val && val.toString().toLowerCase().includes(q);
            });
        });

    const handleSelect = (item) => {
        if (disabled) return;
        const newVal = getLabel(item);
        setQuery(newVal);
        onChange(item);
        setIsOpen(false);
    };

    const handleInput = (e) => {
        if (disabled) return;
        const val = e.target.value;
        setQuery(val);
        setIsOpen(true);
        if (onInputChange) onInputChange(val);
    };

    return (
        <div className={`combobox-container ${className || ''}`} ref={containerRef} style={{ position: 'relative', opacity: disabled ? 0.7 : 1, cursor: disabled ? 'not-allowed' : 'default' }}>
            <div className="relative flex items-center">
                <input
                    type="text"
                    className={`input w-full ${inputClassName || ''}`}
                    style={{ paddingRight: '32px', ...inputStyle, cursor: disabled ? 'not-allowed' : 'text' }}
                    value={query}
                    onChange={handleInput}
                    onFocus={handleFocus}
                    onMouseDown={handleMouseDown}
                    placeholder={placeholder}
                    disabled={disabled}
                />
                <div style={{ position: 'absolute', right: '10px', pointerEvents: 'none', color: 'var(--text-secondary)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </div>
            </div>

            {isOpen && ReactDOM.createPortal(
                <div
                    className="combobox-portal-dropdown"
                    style={{
                        position: 'absolute',
                        top: `${coords.top + 4}px`,
                        left: `${coords.left}px`,
                        width: Math.max(coords.width, 350) + 'px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        zIndex: 9999
                    }}
                >
                    {filteredItems.length > 0 ? (
                        filteredItems.map(item => (
                            <div
                                key={item[valueKey]}
                                onClick={() => handleSelect(item)}
                                className="combobox-item"
                                style={{
                                    padding: '10px 15px',
                                    borderBottom: '1px solid var(--border-light, rgba(255,255,255,0.05))',
                                    backgroundColor: 'var(--bg-card)',
                                    color: 'var(--text-primary)',
                                    cursor: 'pointer'
                                }}
                            >
                                <span style={{ fontWeight: 500 }}>{getLabel(item)}</span>
                                {item.taxNumber && <span className="text-secondary text-xs" style={{ opacity: 0.7, marginLeft: '8px' }}>({item.taxNumber})</span>}
                            </div>
                        ))
                    ) : (
                        <div style={{
                            padding: '12px',
                            color: 'var(--text-secondary)',
                            textAlign: 'center',
                            fontSize: '0.9rem'
                        }}>
                            {query ? `"${query}" bulunamadı.` : 'Veri yok.'}
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default Combobox;
